import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Empty,
    Skeleton,
    Space,
    Switch,
    Tabs,
    Tag,
    Typography,
    message,
} from 'antd';
import { ClearOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';
import {
    ProviderModelTempForm,
    ProviderModelTempValue,
    ProviderId,
} from '../shared/ProviderModelTempForm';

const { Text, Paragraph } = Typography;

interface BriefingAIConfigRow {
    id?: number;
    config_key: string;
    system_prompt: string;
    user_prompt_template: string;
    is_active?: boolean | number;
    [k: string]: unknown;
}

interface BriefingAiPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: {
        briefing_ai_config?: BriefingAIConfigRow[];
    };
}

interface BriefingTypeInfo {
    configKey: string;
    label: string;
    description: string;
}

// Mirrors BRIEFING_TYPES in src/ai-experts/services/briefing-ai.service.ts.
const BRIEFING_TYPES: BriefingTypeInfo[] = [
    {
        configKey: 'one_thing',
        label: 'One thing to know',
        description:
            'The single most important takeaway for the marketer — the Briefing hero. Returns a JSON object: { headline, body, contrast, ctaPrimary, ctaSecondary }.',
    },
    {
        configKey: 'the_fix',
        label: 'The fix',
        description:
            'The highest-leverage thing to fix first, and how. Returns a JSON object: { title, redline, move, expectedEffect }.',
    },
    {
        configKey: 'recommendations',
        label: 'Recommendations',
        description:
            'Prioritised fixes & quick wins for the Recommendations tab / Action Plan. Returns { recommendations: [{ type, title, rationale, objectives: [{ task, detail, impact, effort, moves }] }] }. Can reference the confidential {ELEMENT_WEIGHTINGS_MD} placeholder.',
    },
    {
        configKey: 'boosters_summary',
        label: 'Boosters summary',
        description:
            'A brief plain-English summary of the brand boosters — the caption under the Overall Brand Equity gauge. Returns a JSON object: { summary }.',
    },
];

const PLACEHOLDERS = (
    <span>
        <code>{'{input_text}'}</code> — the brand&apos;s Brand Equity summary
        (overall score, the 7 boosters, signals) built deterministically before
        the call
    </span>
);

function parseModelList(raw: string | undefined): Array<{ id: string; label?: string }> {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown) => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as { id?: string; label?: string };
                    if (e.id) return { id: e.id, label: e.label };
                }
                return null;
            })
            .filter((m): m is { id: string; label?: string } => m !== null);
    } catch {
        return [];
    }
}

function guessProvider(modelId: string | undefined | null): ProviderId {
    if (!modelId) return 'anthropic';
    if (modelId.startsWith('claude') || modelId.startsWith('anthropic')) return 'anthropic';
    return 'openai';
}

function isActiveBool(v: boolean | number | undefined): boolean {
    return v === true || v === 1;
}

/**
 * Configure tab body for The Briefing AI (SYR-68).
 *
 * Enabled toggle + primary/fallback provider config + vertical sidebar tabs,
 * one per briefing narrative type (One thing · The fix). Each type has its own
 * system + user prompt; the runtime injects the brand BE summary via
 * {input_text}. Modelled on Building Blocks AI (per-type prompt config).
 */
export const TheBriefingAiConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<BriefingAiPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [isClearingCache, setIsClearingCache] = useState(false);

    // Mirrors OrbitalAiConfigurePage: publishing clears the cache automatically,
    // but a manual clear forces every brand's briefing to regenerate against the
    // CURRENT published prompts without a publish (e.g. after a data-side fix).
    const clearCache = async () => {
        if (!jwt) return;
        setIsClearingCache(true);
        try {
            const result = await aiAdminApi.clearCache(jwt, 'briefing_ai');
            message.success(
                `Cleared ${result.rowsCleared} row(s) from ${result.tables.join(', ')}.`,
            );
        } catch (err: any) {
            message.error(err.message || 'Failed to clear cache');
        } finally {
            setIsClearingCache(false);
        }
    };
    const [anthropicModels, setAnthropicModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [openaiModels, setOpenaiModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [googleModels, setGoogleModels] = useState<Array<{ id: string; label?: string }>>([]);

    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setAnthropicModels(
                    parseModelList(keys.AVAILABLE_ANTHROPIC_MODELS),
                );
                setOpenaiModels(
                    parseModelList(keys.AVAILABLE_OPENAI_MODELS),
                );
                setGoogleModels(
                    parseModelList(keys.AVAILABLE_GOOGLE_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    const rows = editor.payload.configTableRows?.briefing_ai_config ?? [];

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const setManySettings = (patch: Record<string, string>) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, ...patch },
        }));
    };

    const upsertRow = (configKey: string, patch: Partial<BriefingAIConfigRow>) => {
        editor.setPayload((prev) => {
            const list = prev!.configTableRows.briefing_ai_config ?? [];
            const idx = list.findIndex((r) => r.config_key === configKey);
            const next =
                idx >= 0
                    ? list.map((r, i) => (i === idx ? { ...r, ...patch } : r))
                    : [
                          ...list,
                          {
                              config_key: configKey,
                              system_prompt: '',
                              user_prompt_template: '',
                              is_active: 1,
                              ...patch,
                          },
                      ];
            return {
                ...prev!,
                configTableRows: { ...prev!.configTableRows, briefing_ai_config: next },
            };
        });
    };

    const findRow = (configKey: string): BriefingAIConfigRow | undefined =>
        rows.find((r) => r.config_key === configKey);

    const enabled = (settings.BRIEFING_AI_ENABLED ?? 'true') !== 'false';

    const primaryValue: ProviderModelTempValue = {
        provider: (settings.BRIEFING_AI_PRIMARY_PROVIDER as ProviderId) || guessProvider(settings.BRIEFING_AI_PRIMARY_MODEL),
        model: settings.BRIEFING_AI_PRIMARY_MODEL ?? '',
        temperature: Number(settings.BRIEFING_AI_PRIMARY_TEMPERATURE ?? 0.7),
    };

    const fallbackValue: ProviderModelTempValue = {
        provider: (settings.BRIEFING_AI_FALLBACK_PROVIDER as ProviderId) || guessProvider(settings.BRIEFING_AI_FALLBACK_MODEL),
        model: settings.BRIEFING_AI_FALLBACK_MODEL ?? '',
        temperature: Number(settings.BRIEFING_AI_FALLBACK_TEMPERATURE ?? 0.7),
    };

    const handlePrimaryChange = (next: ProviderModelTempValue) => {
        setManySettings({
            BRIEFING_AI_PRIMARY_PROVIDER: next.provider,
            BRIEFING_AI_PRIMARY_MODEL: next.model,
            BRIEFING_AI_PRIMARY_TEMPERATURE: String(next.temperature),
        });
    };

    const handleFallbackChange = (next: ProviderModelTempValue) => {
        setManySettings({
            BRIEFING_AI_FALLBACK_PROVIDER: next.provider,
            BRIEFING_AI_FALLBACK_MODEL: next.model,
            BRIEFING_AI_FALLBACK_TEMPERATURE: String(next.temperature),
        });
    };

    if (rows.length === 0) {
        return <Empty description="No briefing_ai_config rows — has the v1 baseline been seeded?" />;
    }

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Marketer Briefing narrative generated from Brand Equity data.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Card title="Status" size="small" style={{ marginBottom: 16 }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                    <Space>
                        <Switch
                            checked={enabled}
                            onChange={(checked) =>
                                setSetting('BRIEFING_AI_ENABLED', checked ? 'true' : 'false')
                            }
                            checkedChildren="enabled"
                            unCheckedChildren="disabled"
                        />
                        <Text type="secondary">
                            <code>BRIEFING_AI_ENABLED</code> — when off, the Briefing shows no AI
                            narrative.
                        </Text>
                    </Space>
                    <Button
                        danger
                        icon={<ClearOutlined />}
                        loading={isClearingCache}
                        onClick={clearCache}
                    >
                        Clear briefing cache
                    </Button>
                </Space>
            </Card>

            <Card title="LLM provider — primary + fallback" size="small" style={{ marginBottom: 16 }}>
                <ProviderModelTempForm
                    label="Primary"
                    value={primaryValue}
                    onChange={handlePrimaryChange}
                    anthropicModels={anthropicModels}
                    openaiModels={openaiModels}
                    googleModels={googleModels}
                />
                <div style={{ marginTop: 12 }}>
                    <ProviderModelTempForm
                        label="Fallback"
                        value={fallbackValue}
                        onChange={handleFallbackChange}
                        anthropicModels={anthropicModels}
                        openaiModels={openaiModels}
                        googleModels={googleModels}
                    />
                </div>
            </Card>

            <Card>
                <Tabs
                    tabPosition="left"
                    style={{ minHeight: 600 }}
                    items={BRIEFING_TYPES.map((type) => {
                        const row = findRow(type.configKey);
                        return {
                            key: type.configKey,
                            label: (
                                <span>
                                    {row ? (
                                        <Badge dot status="success" offset={[-4, 0]}>
                                            <span>{type.label}</span>
                                        </Badge>
                                    ) : (
                                        <span>{type.label}</span>
                                    )}
                                </span>
                            ),
                            children: (
                                <RowEditor
                                    row={row}
                                    onChange={(patch) => upsertRow(type.configKey, patch)}
                                    topAlert={
                                        <Alert
                                            type="info"
                                            showIcon
                                            icon={<InfoCircleOutlined />}
                                            style={{ marginBottom: 16 }}
                                            message={type.label}
                                            description={type.description}
                                        />
                                    }
                                    placeholdersAlert={PLACEHOLDERS}
                                />
                            ),
                        };
                    })}
                />
            </Card>

            <Paragraph type="secondary" style={{ marginTop: 16 }}>
                Each type must return ONLY a JSON object (no prose, no code fences). The runtime
                caches the result per (org, brand, type) and regenerates when the Brand Equity
                data or the published prompt changes.
            </Paragraph>
        </div>
    );
};

interface RowEditorProps {
    row: BriefingAIConfigRow | undefined;
    onChange: (patch: Partial<BriefingAIConfigRow>) => void;
    topAlert: React.ReactNode;
    placeholdersAlert: React.ReactNode;
}

const RowEditor: React.FC<RowEditorProps> = ({
    row,
    onChange,
    topAlert,
    placeholdersAlert,
}) => {
    return (
        <div>
            {topAlert}

            {row && (
                <Space style={{ marginBottom: 12 }} wrap>
                    <Tag color={isActiveBool(row.is_active) ? 'green' : 'default'}>
                        {isActiveBool(row.is_active) ? 'active' : 'inactive'}
                    </Tag>
                    <Switch
                        checked={isActiveBool(row.is_active)}
                        onChange={(checked) => onChange({ is_active: checked ? 1 : 0 })}
                        checkedChildren="on"
                        unCheckedChildren="off"
                    />
                </Space>
            )}

            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="briefing_ai"
                    label="System Prompt"
                    value={row?.system_prompt ?? ''}
                    onChange={(v) => onChange({ system_prompt: v })}
                    rows={8}
                />
            </div>
            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="briefing_ai"
                    label="User Prompt Template"
                    value={row?.user_prompt_template ?? ''}
                    onChange={(v) => onChange({ user_prompt_template: v })}
                    rows={14}
                />
            </div>

            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message="Available Placeholders"
                description={placeholdersAlert}
            />
        </div>
    );
};
